import type { JSX } from "react";
import { cn } from "./cn";

export interface MiniCalendarProps {
  readonly selectedDate?: Date;
  readonly displayMonth: Date;
  readonly onDateClick: (date: Date) => void;
  readonly onMonthChange: (delta: -1 | 1) => void;
  readonly className?: string;
}

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"] as const;
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

interface DayCell {
  readonly date: Date;
  readonly inMonth: boolean;
}

function buildGrid(displayMonth: Date): readonly DayCell[] {
  const year = displayMonth.getFullYear();
  const month = displayMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: DayCell[] = [];

  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    cells.push({ date: d, inMonth: false });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ date: new Date(year, month, day), inMonth: true });
  }

  const remaining = 42 - cells.length;
  for (let i = 1; i <= remaining; i++) {
    cells.push({ date: new Date(year, month + 1, i), inMonth: false });
  }

  return cells;
}

export function MiniCalendar({
  selectedDate,
  displayMonth,
  onDateClick,
  onMonthChange,
  className,
}: MiniCalendarProps): JSX.Element {
  const today = new Date();
  const cells = buildGrid(displayMonth);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const monthLabel = `${MONTH_NAMES[displayMonth.getMonth()]} ${displayMonth.getFullYear()}`;

  return (
    <div className={cn("select-none", className)}>
      {/* Month header with nav */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          className="border-none bg-transparent cursor-pointer text-content-secondary hover:text-content p-0.5 text-[11px] leading-none"
          onClick={() => { onMonthChange(-1); }}
        >
          ‹
        </button>
        <span className="text-[11px] font-semibold text-content">{monthLabel}</span>
        <button
          type="button"
          className="border-none bg-transparent cursor-pointer text-content-secondary hover:text-content p-0.5 text-[11px] leading-none"
          onClick={() => { onMonthChange(1); }}
        >
          ›
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAY_LABELS.map((label, i) => (
          <div
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            key={`${label}-${i}`}
            className="text-[9px] font-medium text-content-tertiary text-center leading-tight py-0.5"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((cell) => {
          const isSelected = selectedDate ? isSameDay(cell.date, selectedDate) : false;
          const isTodayCell = isSameDay(cell.date, today);

          return (
            <button
              key={cell.date.toISOString()}
              type="button"
              className={cn(
                "border-none cursor-pointer p-0 flex flex-col items-center justify-center",
                "h-7 w-7 text-[11px] leading-none rounded-full mx-auto",
                isSelected
                  ? "bg-orange-600 text-white font-bold"
                  : isTodayCell
                    ? "bg-transparent text-orange-500 font-bold"
                    : cell.inMonth
                      ? "bg-transparent text-content hover:bg-surface-hover"
                      : "bg-transparent text-content-tertiary opacity-30",
              )}
              onClick={() => { onDateClick(cell.date); }}
            >
              {cell.date.getDate()}
              {isTodayCell && !isSelected && (
                <span className="w-1 h-1 rounded-full bg-orange-500 -mt-0.5" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
